/** @jsxImportSource @emotion/react */
import { EmptyData } from '@app/components/ui/EmptyData/EmptyData'
import { isLoadableError } from '@app/state/jotai.utils'
import { Box, Container, Tab, Tabs } from '@mui/material'
import { useAtomValue } from 'jotai'
import { loadable } from 'jotai/utils'
import { DatabaseIcon } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import {
  Outlet,
  Link as RouterLink,
  useLocation,
  useMatch,
  useResolvedPath,
} from 'react-router-dom'
import { layersAtom } from '@app/domains/layers/state/layers.atoms'
import * as styles from '../styles/DataLayout.styles'
import { FEATURE_FLAGS } from '@app/app-constants'
import { useFeatureFlags } from '@app/hooks/useFeatureFlags'

const layersLoadable = loadable(layersAtom)

// `<main>` (the nearest scrolling ancestor, from HomeV2Layout) is taller than
// the space actually available below its in-flow navbar, so `height: 100%`
// here would overflow by the navbar's height. Measure the real gap instead.
const useAvailableHeight = () => {
  const rootRef = useRef<HTMLDivElement>(null)
  const [height, setHeight] = useState<number | null>(null)

  useEffect(() => {
    const root = rootRef.current
    if (!root) return

    const scrollParent = root.closest('main') ?? root.parentElement
    if (!scrollParent) return

    const updateHeight = () => {
      const available = scrollParent.clientHeight - root.getBoundingClientRect().top
      setHeight(Math.max(available, 0))
    }

    updateHeight()

    const observer = new ResizeObserver(updateHeight)
    observer.observe(scrollParent)

    return () => observer.disconnect()
  }, [])

  return { rootRef, height }
}

const DataLayout = () => {
  const layers = useAtomValue(layersLoadable)
  const location = useLocation()
  const basePath = useResolvedPath('.').pathname
  const isDataRiskDetailPage = useMatch('/app/home/data/detail/:id')
  const { useFeatureFlag } = useFeatureFlags()
  const isHazardReferenceEnabled = useFeatureFlag(FEATURE_FLAGS.HAZARD_REFERENCE)
  const { rootRef, height } = useAvailableHeight()

  const normalizePath = (p: string) => (p.length > 1 ? p.replace(/\/+$/, '') : p)

  const base = normalizePath(basePath)
  const current = normalizePath(location.pathname)

  const tabLink = useMemo(
    () => ({
      all: base,
      elements: `${base}/elements`,
      hazards: `${base}/hazards`,
      contextual: `${base}/contextual`,
      reference: `${base}/reference`,
    }),
    [base],
  )

  if (isLoadableError(layers)) {
    return <EmptyData title="Error loading layers" icon={<DatabaseIcon />} />
  }

  return (
    <Box
      ref={rootRef}
      data-testid="risk-data-layout"
      css={styles.layoutWrapper}
      style={height !== null ? { height } : undefined}
    >
      <Box css={styles.container}>
        {!isDataRiskDetailPage && (
          <Box css={styles.hazardTableContainer}>
            <Box css={styles.tabsWrapper}>
              <Tabs value={current}>
                <Tab label="All" value={tabLink.all} component={RouterLink} to="" replace />
                <Tab
                  label="Elements"
                  value={tabLink.elements}
                  component={RouterLink}
                  to="elements"
                  replace
                />
                <Tab
                  label="Hazards"
                  value={tabLink.hazards}
                  component={RouterLink}
                  to="hazards"
                  replace
                />
                <Tab
                  label="Contextual"
                  value={tabLink.contextual}
                  component={RouterLink}
                  to="contextual"
                  replace
                />
                {isHazardReferenceEnabled && (
                  <Tab
                    label="Reference"
                    value={tabLink.reference}
                    component={RouterLink}
                    to="reference"
                    replace
                  />
                )}
              </Tabs>
            </Box>
          </Box>
        )}

        <Box data-testid="risk-data-layout-outlet">
          <Container css={styles.hazardTableContainer}>
            <Outlet />
          </Container>
        </Box>
      </Box>
    </Box>
  )
}

export default DataLayout
